import React from 'react';
import { CheckCircle2, Award, Users, Clock } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { labInfo, heroImages } from '../mockData';

const About = () => {
  const highlights = [
    {
      icon: Award,
      title: 'Advanced Technology',
      description: 'State-of-the-art diagnostic equipment for accurate results'
    },
    {
      icon: Users,
      title: 'Experienced Team',
      description: 'Qualified technicians and healthcare professionals'
    },
    {
      icon: CheckCircle2,
      title: 'Accurate Reports',
      description: 'Reliable and precise diagnostic reports'
    },
    {
      icon: Clock,
      title: 'Quick Turnaround',
      description: 'Fast processing and result delivery'
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="bg-gradient-to-b from-blue-50 to-white py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              About JKDC Lab
            </h1>
            <p className="text-lg text-gray-600">
              Your trusted partner for accurate medical diagnostics in Trivandrum
            </p>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-16">
            <div>
              <img
                src={heroImages.main}
                alt="JKDC Lab Facility"
                className="rounded-2xl shadow-xl w-full h-[400px] object-cover"
              />
            </div>
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-6">
                Committed to Excellence in Diagnostic Care
              </h2>
              <p className="text-gray-600 mb-4">
                Established on {labInfo.established}, JKDC Lab has been serving the Trivandrum community with advanced medical diagnostic services. Located conveniently at {labInfo.address.line1}, {labInfo.address.line2}, we are easily accessible to patients across the region.
              </p>
              <p className="text-gray-600 mb-4">
                Our facility is equipped with the latest diagnostic technology, including Digital X-Ray, Dental X-Ray (OPG), and 12-Channel ECG equipment. We are committed to providing accurate and timely diagnostic results to help healthcare providers make informed decisions about patient care.
              </p>
              <p className="text-gray-600">
                At JKDC Lab, we understand the importance of precision in medical diagnostics. Our team of experienced technicians ensures that every test is conducted with the highest standards of quality and care.
              </p>
            </div>
          </div>

          {/* Highlights */}
          <div className="mb-16">
            <h2 className="text-3xl font-bold text-gray-900 text-center mb-12">
              What Sets Us Apart
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {highlights.map((highlight, index) => {
                const IconComponent = highlight.icon;
                return (
                  <Card key={index} className="border-none shadow-lg hover:shadow-xl transition-shadow">
                    <CardContent className="p-6 text-center">
                      <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                        <IconComponent className="w-8 h-8 text-blue-600" />
                      </div>
                      <h3 className="text-lg font-bold text-gray-900 mb-2">
                        {highlight.title}
                      </h3>
                      <p className="text-gray-600 text-sm">
                        {highlight.description}
                      </p>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>

          {/* Mission & Values */}
          <div className="bg-gray-50 rounded-2xl p-8 md:p-12">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl font-bold text-gray-900 mb-6 text-center">
                Our Commitment
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">Mission</h3>
                  <p className="text-gray-600">
                    To provide accessible, accurate, and affordable diagnostic services that contribute to better healthcare outcomes for our community.
                  </p>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">Values</h3>
                  <ul className="space-y-2">
                    <li className="flex items-start space-x-2">
                      <CheckCircle2 className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-600">Accuracy and Precision</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <CheckCircle2 className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-600">Patient-Centric Care</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <CheckCircle2 className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-600">Professional Excellence</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Image Placeholder Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-12">
            Our Facility
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[1, 2, 3].map((item) => (
              <div key={item} className="bg-gray-100 rounded-lg h-64 flex items-center justify-center">
                <p className="text-gray-500 text-center px-4">
                  [Your facility photo {item} here]
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
